import { useMemo } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';

interface Row { application_id: string; campaign_id: string; unread: number }

/**
 * Unread campaign-chat message counts for the signed-in user, across every thread
 * they participate in (as a business or a creator). Polls every 20s. Returns
 * lookups keyed by application (per chat) and by campaign (business aggregate),
 * plus the grand total for a nav-tab badge.
 */
export function useUnreadThreads() {
  const { user } = useAuth();
  const { data } = useSupabaseQuery<Row[]>(
    ['unread-threads', user?.id],
    async () => supabase.rpc('get_my_unread_threads'),
    { enabled: !!user, refetchInterval: 20_000, refetchOnWindowFocus: true, staleTime: 8_000 },
  );
  return useMemo(() => {
    const byApplication = new Map<string, number>();
    const byCampaign = new Map<string, number>();
    let total = 0;
    for (const r of data ?? []) {
      const u = Number(r.unread) || 0;
      if (u <= 0) continue;
      byApplication.set(r.application_id, u);
      byCampaign.set(r.campaign_id, (byCampaign.get(r.campaign_id) ?? 0) + u);
      total += u;
    }
    return { byApplication, byCampaign, total };
  }, [data]);
}
